import React, { useState, useEffect } from 'react';
import Collapse from '../Common/Collapse';
import useDragSort from '../../hooks/useDragSort';
import DragHandle from '../Common/DragHandle';
import { reorderCourses, reorderSections, reorderLessons, reorderHomeworks } from '../../services/adminService';
import toast from 'react-hot-toast';

/* Дерево «Задания» с живым перетаскиванием на всех уровнях:
   курсы → модули → уроки → номера ДЗ. Тащится вся строка.
   Порядок хранится в локальном state `tree` и сохраняется на бэкенд при отпускании. */

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const DISPLAY = "'Unbounded', 'Manrope', sans-serif";

// Палитра акцентов курсов: каждый курс получает свой цвет по порядку.
// Новый курс автоматически берёт следующий незанятый оттенок.
const COURSE_PALETTE = [
  { accent: '#16a34a', soft: 'rgba(34,197,94,0.13)',  head: 'linear-gradient(90deg,rgba(34,197,94,0.10),transparent 42%)' },
  { accent: '#0891b2', soft: 'rgba(8,145,178,0.13)',  head: 'linear-gradient(90deg,rgba(8,145,178,0.10),transparent 42%)' },
  { accent: '#d97706', soft: 'rgba(245,158,11,0.16)', head: 'linear-gradient(90deg,rgba(245,158,11,0.12),transparent 42%)' },
  { accent: '#7c4dff', soft: 'rgba(124,77,255,0.14)', head: 'linear-gradient(90deg,rgba(124,77,255,0.12),transparent 42%)' },
  { accent: '#db2777', soft: 'rgba(219,39,119,0.13)', head: 'linear-gradient(90deg,rgba(219,39,119,0.12),transparent 42%)' },
  { accent: '#2563eb', soft: 'rgba(37,99,235,0.13)',  head: 'linear-gradient(90deg,rgba(37,99,235,0.12),transparent 42%)' },
];

// ── Иконки ───────────────────────────────────────────────────────────────────
const Chev = ({ open, color = 'var(--text-3)', size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: 'transform 0.22s cubic-bezier(.34,1.2,.5,1)', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0, display: 'inline-flex' }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const IcoBookOpen = ({ s = 22 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);
const IcoCap = ({ s = 22 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
  </svg>
);
const IcoLayers = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
  </svg>
);
const IcoClipCheck = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="2" /><path d="M9 14l2 2 4-4" />
  </svg>
);
const IcoEditSm = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const IcoTrashSm = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);
const IcoPlus = ({ s = 12 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
);

// ── Сборка дерева из плоского списка ДЗ + курсов (сортировка по sortOrder) ─────
const bySort = (a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id);

function buildTree(homeworks, courses) {
  const courseMap = new Map();
  homeworks.forEach(hw => {
    const lesson = hw.Lesson;
    const section = lesson?.Section;
    const cId = section?.courseId ?? 0;
    if (!courseMap.has(cId)) {
      const cObj = courses.find(c => c.id === cId);
      courseMap.set(cId, { id: cId, name: cObj?.name || 'Без курса', sortOrder: cObj?.sortOrder ?? 0, _sections: new Map() });
    }
    const c = courseMap.get(cId);
    const sId = section?.id ?? 0;
    if (!c._sections.has(sId)) {
      c._sections.set(sId, { id: sId, name: section?.name || 'Без раздела', sortOrder: section?.sortOrder ?? 0, _lessons: new Map() });
    }
    const s = c._sections.get(sId);
    const lId = lesson?.id ?? 0;
    if (!s._lessons.has(lId)) {
      s._lessons.set(lId, { id: lId, name: lesson?.title || 'Без урока', sortOrder: lesson?.sortOrder ?? 0, homeworks: [] });
    }
    s._lessons.get(lId).homeworks.push(hw);
  });
  return [...courseMap.values()].sort(bySort).map(c => ({
    id: c.id, name: c.name, sortOrder: c.sortOrder,
    sections: [...c._sections.values()].sort(bySort).map(s => ({
      id: s.id, name: s.name, sortOrder: s.sortOrder,
      lessons: [...s._lessons.values()].sort(bySort).map(l => ({
        id: l.id, name: l.name, sortOrder: l.sortOrder,
        homeworks: l.homeworks.slice().sort(bySort),
      })),
    })),
  }));
}

const pill = (bg, color) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 30, fontSize: 12, fontWeight: 800, background: bg, color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' });

const hwNum = (hw, i) => {
  const m = String(hw.title || '').match(/\d+/);
  return String(m ? m[0] : i + 1).padStart(2, '0');
};

// Русское склонение: plural(n, ['модуль','модуля','модулей'])
const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};
const FORMS = {
  module: ['модуль', 'модуля', 'модулей'],
  task: ['задание', 'задания', 'заданий'],
  num: ['номер', 'номера', 'номеров'],
};

// ── Лист: номера ДЗ урока (по одному в строке) ────────────────────────────────
function HomeworkLeaves({ lesson, onEdit, onDelete, onReorder, onCommit }) {
  const { getHandleProps, getItemProps } = useDragSort(lesson.homeworks, onReorder, onCommit);
  return (
    <div className="hw-leaves" style={{ margin: '9px 0 6px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {lesson.homeworks.map((hw, i) => {
        const grid = hw.answerType === 'grid';
        return (
          <div key={hw.id} {...getItemProps(hw.id)} onClick={() => onEdit(hw)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 13, cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,77,255,0.5)'; e.currentTarget.style.boxShadow = '0 6px 18px -10px rgba(124,77,255,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
            <span style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 13, fontWeight: 700, background: 'var(--primary-light)', color: 'var(--primary-dark)' }}>{hwNum(hw, i)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hw.title || 'Задание'}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 1 }}>{grid ? `Сетка ${hw.gridRows || 3}×${hw.gridCols || 2}` : 'Одно значение'}</div>
            </div>
            <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap', background: grid ? 'var(--primary-light)' : 'rgba(34,197,94,0.15)', color: grid ? 'var(--primary-dark)' : '#16a34a' }}>{grid ? 'Сетка' : 'Число'}</span>
            <div style={{ display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center' }}>
              <button onClick={e => { e.stopPropagation(); onEdit(hw); }}
                style={{ width: 28, height: 28, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--card)', color: 'var(--text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)'; }}><IcoEditSm /></button>
              <button onClick={e => { e.stopPropagation(); onDelete(hw.id); }}
                style={{ width: 28, height: 28, border: '1px solid rgba(229,72,77,0.3)', borderRadius: 8, background: 'rgba(229,72,77,0.08)', color: '#e5484d', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(229,72,77,0.18)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(229,72,77,0.08)'}><IcoTrashSm /></button>
              <DragHandle {...getHandleProps(hw.id)} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Урок ──────────────────────────────────────────────────────────────────────
function LessonNode({ course, section, lesson, dragProps, handleProps, exp, onEdit, onDelete, onQuickAdd, reorderHw, commitHw }) {
  const open = !!exp.lessons[lesson.id];
  return (
    <div>
      <div {...dragProps} onClick={() => exp.toggleLesson(lesson.id)} className="hw-lesson-row"
        style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 15px', borderRadius: 14, cursor: 'pointer', userSelect: 'none', background: 'var(--card)', border: '1px solid var(--border)', transition: 'border-color .15s, box-shadow .15s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,77,255,0.4)'; e.currentTarget.style.boxShadow = '0 8px 22px -14px rgba(124,77,255,0.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
        <Chev open={open} />
        <div style={{ width: 36, height: 36, borderRadius: 11, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary-light)', color: 'var(--primary)' }}><IcoClipCheck /></div>
        <span style={{ fontFamily: DISPLAY, flex: 1, fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lesson.name}</span>
        <span style={pill('rgba(34,197,94,0.13)', '#16a34a')}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a' }} />{lesson.homeworks.length} {plural(lesson.homeworks.length, FORMS.num)}</span>
        <button onClick={e => { e.stopPropagation(); onQuickAdd(lesson.id, { sectionId: section.id, courseId: course.id }); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 13px', border: '1.5px dashed rgba(124,77,255,0.45)', borderRadius: 10, fontSize: 11.5, fontWeight: 700, color: 'var(--primary)', background: 'none', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary-light)'; e.currentTarget.style.borderStyle = 'solid'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderStyle = 'dashed'; }}>
          <IcoPlus />Добавить
        </button>
        <DragHandle {...handleProps} />
      </div>
      <Collapse open={open}>
        <HomeworkLeaves lesson={lesson} onEdit={onEdit} onDelete={onDelete}
          onReorder={(ids) => reorderHw(course.id, section.id, lesson.id, ids)} onCommit={commitHw} />
      </Collapse>
    </div>
  );
}

// ── Раздел (модуль) ────────────────────────────────────────────────────────────
function SectionNode({ course, section, dragProps, handleProps, exp, onEdit, onDelete, onQuickAdd, reorderLessonsLive, commitLessons, reorderHw, commitHw }) {
  const open = !!exp.sections[section.id];
  const secTasks = section.lessons.length;   // заданий в модуле (уроков), не номеров
  const lessonSort = useDragSort(section.lessons, (ids) => reorderLessonsLive(course.id, section.id, ids), commitLessons);
  return (
    <div>
      <div {...dragProps} onClick={() => exp.toggleSection(section.id)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderRadius: 13, cursor: 'pointer', userSelect: 'none', background: 'var(--primary-light)', border: '1px solid transparent', transition: 'border-color .15s' }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(124,77,255,0.3)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}>
        <Chev open={open} color="var(--primary)" />
        <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--card)', color: 'var(--primary)' }}><IcoLayers /></div>
        <span style={{ fontFamily: DISPLAY, flex: 1, fontSize: 16.5, fontWeight: 600, letterSpacing: '-0.3px', color: 'var(--text-1)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{section.name}</span>
        <span style={{ padding: '4px 11px', borderRadius: 30, fontSize: 11.5, fontWeight: 800, background: 'var(--card)', color: 'var(--primary-dark)', fontVariantNumeric: 'tabular-nums' }}>{secTasks} {plural(secTasks, FORMS.task)}</span>
        <DragHandle {...handleProps} />
      </div>
      <Collapse open={open}>
        <div className="hw-sbody" style={{ margin: '12px 0 0 26px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {section.lessons.map(lesson => (
            <LessonNode key={lesson.id} course={course} section={section} lesson={lesson}
              dragProps={lessonSort.getItemProps(lesson.id)} handleProps={lessonSort.getHandleProps(lesson.id)} exp={exp}
              onEdit={onEdit} onDelete={onDelete} onQuickAdd={onQuickAdd} reorderHw={reorderHw} commitHw={commitHw} />
          ))}
        </div>
      </Collapse>
    </div>
  );
}

// ── Курс ──────────────────────────────────────────────────────────────────────
function CourseNode({ course, idx, dragProps, handleProps, exp, onEdit, onDelete, onQuickAdd, reorderSectionsLive, commitSections, reorderLessonsLive, commitLessons, reorderHw, commitHw }) {
  const open = !!exp.courses[course.id];
  const isOge = course.name.includes('ОГЭ');
  // Считаем ЗАДАНИЯ (уроки с ДЗ), а не номера внутри них.
  const totalTasks = course.sections.reduce((t, s) => t + s.lessons.length, 0);
  const pal = COURSE_PALETTE[idx % COURSE_PALETTE.length];
  const sectionSort = useDragSort(course.sections, (ids) => reorderSectionsLive(course.id, ids), commitSections);
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 18, overflow: 'hidden', boxShadow: '0 10px 30px -22px rgba(40,20,80,0.45)' }}>
      <div {...dragProps} onClick={() => exp.toggleCourse(course.id)}
        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 15, padding: '18px 22px 18px 24px', cursor: 'pointer', userSelect: 'none', background: pal.head }}>
        <span style={{ position: 'absolute', left: 0, top: 12, bottom: 12, width: 5, borderRadius: '0 5px 5px 0', background: pal.accent, boxShadow: `0 0 16px -2px ${pal.accent}` }} />
        <Chev open={open} />
        <div style={{ width: 50, height: 50, borderRadius: 15, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: pal.soft, color: pal.accent }}>
          {isOge ? <IcoBookOpen /> : <IcoCap />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: DISPLAY, fontSize: 17, fontWeight: 600, letterSpacing: '-0.4px', color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.name}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{course.sections.length} {plural(course.sections.length, FORMS.module)}</div>
        </div>
        <span style={pill('var(--primary-light)', 'var(--primary-dark)')}>{totalTasks} {plural(totalTasks, FORMS.task)}</span>
        <DragHandle {...handleProps} />
      </div>
      <Collapse open={open}>
        <div className="hw-cbody" style={{ padding: '4px 22px 18px 30px' }}>
          <div className="hw-spine" style={{ borderLeft: '2px solid var(--border)', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {course.sections.map(section => (
              <SectionNode key={section.id} course={course} section={section}
                dragProps={sectionSort.getItemProps(section.id)} handleProps={sectionSort.getHandleProps(section.id)} exp={exp}
                onEdit={onEdit} onDelete={onDelete} onQuickAdd={onQuickAdd}
                reorderLessonsLive={reorderLessonsLive} commitLessons={commitLessons}
                reorderHw={reorderHw} commitHw={commitHw} />
            ))}
          </div>
        </div>
      </Collapse>
    </div>
  );
}

// ── Дерево целиком ────────────────────────────────────────────────────────────
export default function HomeworkTree({ homeworks, courses, exp, onEdit, onDelete, onQuickAdd }) {
  const [tree, setTree] = useState(() => buildTree(homeworks, courses));
  useEffect(() => { setTree(buildTree(homeworks, courses)); }, [homeworks, courses]);

  const fail = () => toast.error('Не удалось сохранить порядок');

  const reorderCoursesLive = (ids) => setTree(prev => ids.map(id => prev.find(c => c.id === id)).filter(Boolean));
  const commitCourses = (ids) => reorderCourses(ids).catch(fail);

  const reorderSectionsLive = (courseId, ids) => setTree(prev => prev.map(c =>
    c.id !== courseId ? c : { ...c, sections: ids.map(id => c.sections.find(s => s.id === id)).filter(Boolean) }));
  const commitSections = (ids) => reorderSections(ids).catch(fail);

  const reorderLessonsLive = (courseId, sectionId, ids) => setTree(prev => prev.map(c =>
    c.id !== courseId ? c : { ...c, sections: c.sections.map(s =>
      s.id !== sectionId ? s : { ...s, lessons: ids.map(id => s.lessons.find(l => l.id === id)).filter(Boolean) }) }));
  const commitLessons = (ids) => reorderLessons(ids).catch(fail);

  const reorderHw = (courseId, sectionId, lessonId, ids) => setTree(prev => prev.map(c =>
    c.id !== courseId ? c : { ...c, sections: c.sections.map(s =>
      s.id !== sectionId ? s : { ...s, lessons: s.lessons.map(l =>
        l.id !== lessonId ? l : { ...l, homeworks: ids.map(id => l.homeworks.find(h => h.id === id)).filter(Boolean) }) }) }));
  const commitHw = (ids) => reorderHomeworks(ids).catch(fail);

  const courseSort = useDragSort(tree, reorderCoursesLive, commitCourses);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {tree.map((course, idx) => (
        <CourseNode key={course.id} course={course} idx={idx}
          dragProps={courseSort.getItemProps(course.id)} handleProps={courseSort.getHandleProps(course.id)} exp={exp}
          onEdit={onEdit} onDelete={onDelete} onQuickAdd={onQuickAdd}
          reorderSectionsLive={reorderSectionsLive} commitSections={commitSections}
          reorderLessonsLive={reorderLessonsLive} commitLessons={commitLessons}
          reorderHw={reorderHw} commitHw={commitHw} />
      ))}
    </div>
  );
}
